import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

/** Autocomplete chip picker — matches the pattern used in Admin > Users. */
function ChipPicker({ label, hint, options, values, onChange, placeholder, listId, testid }) {
  const [input, setInput] = useState("");
  const commit = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { setInput(""); return; }
    onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <div className="label-tiny mb-1">{label}</div>
      <div className="flex gap-2">
        <input
          list={listId}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(input); } }}
          onBlur={() => { if (input.trim()) commit(input); }}
          placeholder={placeholder}
          className="field-input flex-1"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        <button type="button" onClick={() => commit(input)} className="btn-secondary !py-2 text-xs whitespace-nowrap">
          Add
        </button>
      </div>
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
      {hint && <div className="text-[10px] text-stone-500 mt-1">{hint}</div>}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {values.map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1 border border-stone-700 bg-stone-900 px-2 py-0.5 text-xs">
              {v}
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="text-stone-500 hover:text-rose-400">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Signup() {
  const { user, loading, signup } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [linkedRiders, setLinkedRiders] = useState([]);
  const [linkedHorses, setLinkedHorses] = useState([]);
  const [riderOptions, setRiderOptions] = useState([]);
  const [horseOptions, setHorseOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  // Load autocomplete suggestions from public endpoints
  useEffect(() => {
    api.get("/riders").then((r) => setRiderOptions((r.data.riders || []).map((x) => x.name))).catch(() => {});
    api.get("/horses").then((r) => setHorseOptions((r.data.horses || []).map((x) => x.name))).catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">…</div>;
  if (user) return <Navigate to={user.role === "client" ? "/portal" : "/dashboard"} replace />;

  const toggleDiscipline = (id) => {
    setDisciplines((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const u = await signup({
        email: email.trim(),
        password,
        name: name.trim(),
        phone_number: phone.trim(),
        disciplines,
        linked_rider_names: linkedRiders,
        linked_horse_names: linkedHorses,
      });
      toast.success(`Welcome to Rodeo Cut Studio${u.name ? `, ${u.name}` : ""}!`);
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="label-tiny mb-2">Rodeo Cut Studio</div>
          <h1 className="font-display uppercase text-4xl font-bold">Create your account</h1>
          <p className="text-stone-400 text-sm mt-3 max-w-md mx-auto">
            Find your runs, buy your clips, and unlock the editor when you're ready. Free to sign up — upgrade any time.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6 border border-stone-800 p-6" data-testid="signup-form">
          {/* --- Account basics --- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label-tiny mb-1">Your name</div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                className="field-input w-full"
                data-testid="signup-name"
              />
            </div>
            <div>
              <div className="label-tiny mb-1">Email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="field-input w-full"
                data-testid="signup-email"
              />
            </div>
            <div>
              <div className="label-tiny mb-1">Password</div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                className="field-input w-full"
                data-testid="signup-password"
              />
              <div className="text-[10px] text-stone-500 mt-1">Min 6 characters</div>
            </div>
            <div>
              <div className="label-tiny mb-1">
                Cell <span className="text-stone-500 normal-case tracking-normal">(optional — add later in settings)</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="+1 555 123 4567"
                className="field-input w-full"
                data-testid="signup-phone"
              />
            </div>
          </div>

          {/* --- Disciplines --- */}
          <div>
            <div className="label-tiny mb-2">What do you compete in / follow?</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="signup-disciplines">
              {CATEGORIES.map((cat) => {
                const on = disciplines.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleDiscipline(cat.id)}
                    className={`text-left text-xs uppercase tracking-widest border px-3 py-2 transition-colors ${
                      on
                        ? "border-orange-600 bg-orange-600/10 text-orange-300"
                        : "border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-200"
                    }`}
                    data-testid={`signup-discipline-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-stone-500 mt-2">
              We'll use these to auto-link matching clips to your account and filter your feed.
            </div>
          </div>

          {/* --- Linked riders/horses --- */}
          <ChipPicker
            label="Your rider name(s)"
            hint="Add any name you compete under so we can find your runs (e.g. Sarah Smith, S. Smith)"
            options={riderOptions}
            values={linkedRiders}
            onChange={setLinkedRiders}
            placeholder="Type a name and press Enter"
            listId="signup-rider-suggest"
            testid="signup-rider"
          />
          <ChipPicker
            label="Your horse name(s)"
            hint="Optional — helps us match clips even when your name is missing on the video"
            options={horseOptions}
            values={linkedHorses}
            onChange={setLinkedHorses}
            placeholder="Type a horse name and press Enter"
            listId="signup-horse-suggest"
            testid="signup-horse"
          />

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3"
            data-testid="signup-submit"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Creating account…</> : "Create account"}
          </button>

          <p className="text-[10px] text-stone-500 text-center">
            By creating an account you agree to our terms. We'll never spam you.
          </p>
        </form>

        <p className="text-center text-xs text-stone-500 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-orange-400 hover:text-orange-300" data-testid="signup-goto-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
